import React, { useState } from "react";
import { safeJSONParse } from "../../../utils/helper";
import styles from "../../EvaluationCard/evaluationcard.module.scss";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  IconButton,
  TextField,
  Button,
  Chip,
  Box,
  Typography,
  Paper,
  Tooltip,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { setTemplateReviewerFeedback } from "../../../store/filtersSlice";

type TemplateReviewerAgentProps = {
  parsedContent: any;
};

// Helper function to get severity colors
const getSeverityColor = (severity: string) => {
  const severityLower = severity?.toLowerCase();
  switch (severityLower) {
    case "high":
      return { bg: "#fee", border: "#dc3545", text: "#dc3545", label: "High" };
    case "medium":
      return {
        bg: "#fff3cd",
        border: "#ffc107",
        text: "#856404",
        label: "Medium",
      };
    case "low":
      return {
        bg: "#d4edda",
        border: "#28a745",
        text: "#155724",
        label: "Low",
      };
    default:
      return {
        bg: "#e3f2fd",
        border: "#00a888",
        text: "#00a888",
        label: severity,
      };
  }
};

// Copyable Text Component with Copy Button and Action Buttons
const CopyableText = ({
  text,
  label,
  backgroundColor,
  borderColor,
  textColor,
  onAccept,
  onReject,
  onModify,
  showActions = true,
}: {
  text: string;
  label: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  onAccept?: () => void;
  onReject?: () => void;
  onModify?: () => void;
  showActions?: boolean;
}) => {
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  return (
    <div
      style={{
        background: "#f0fbf9",
        padding: "12px",
        borderRadius: "6px",
        border: "1px solid #B2E5DE",
        position: "relative",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "8px",
        }}
      >
        <p
          style={{
            fontSize: "14px",
            color: "#004141",
            fontWeight: "500",
            margin: 0,
            flex: 1,
          }}
        >
          <strong>{label}:</strong> {text}
        </p>

        {/* Button Group - Action buttons + Copy button */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            opacity: isHovered ? 1 : 0,
            transition: "opacity 0.2s ease-in-out",
            pointerEvents: isHovered ? "auto" : "none",
          }}
        >
          {showActions && onAccept && (
            <Tooltip title="Accept" arrow>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onAccept();
                }}
                size="small"
                sx={{
                  padding: "4px",
                  color: "#39a906",
                  minWidth: "24px",
                  "&:hover": {
                    backgroundColor: "#dcfce7",
                  },
                }}
              >
                <CheckCircleIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </Tooltip>
          )}

          {showActions && onReject && (
            <Tooltip title="Reject" arrow>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onReject();
                }}
                size="small"
                sx={{
                  padding: "4px",
                  color: "#bd1919",
                  minWidth: "24px",
                  "&:hover": {
                    backgroundColor: "#fee",
                  },
                }}
              >
                <CancelIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </Tooltip>
          )}

          {showActions && onModify && (
            <Tooltip title="Modify" arrow>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onModify();
                }}
                size="small"
                sx={{
                  padding: "4px",
                  color: "#00a888",
                  minWidth: "24px",
                  "&:hover": {
                    backgroundColor: "#e3f2fd",
                  },
                }}
              >
                <EditIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={copied ? "Copied!" : "Copy to clipboard"} arrow>
            <IconButton
              onClick={handleCopy}
              size="small"
              sx={{
                padding: "4px",
                color: textColor,
                minWidth: "24px",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.08)",
                },
              }}
            >
              <ContentCopyIcon sx={{ fontSize: "16px" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </div>
  );
};

// Missing Pieces Accordion Item
const MissingPieceItem = ({
  section,
  severity,
  description,
  recommendation,
  isOpen,
  onToggle,
  onAccept,
  onReject,
  onModify,
  actionStatus,
  modifyText,
  onModifyTextChange,
  onModifySave,
}: any) => {
  const severityColors = getSeverityColor(severity);

  return (
    <div className={styles.cardContainer}>
      <div className={styles.cardHeader}>
        <div
          onClick={onToggle}
          style={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            cursor: "pointer",
          }}
        >
          <h2 className={styles.cardTitle}>
            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            {/* <span style={{ fontWeight: "500", marginRight: "8px" }}>
              Rationale:
            </span> */}
            {section}
            <Chip
              label={severityColors.label}
              size="small"
              sx={{
                backgroundColor: severityColors.bg,
                color: severityColors.text,
                border: `1px solid ${severityColors.border}`,
                fontWeight: 600,
                fontSize: "11px",
                marginLeft: "12px",
              }}
            />
            {actionStatus && (
              <Chip
                label={actionStatus}
                size="small"
                color={
                  actionStatus === "Accepted"
                    ? "success"
                    : actionStatus === "Rejected"
                      ? "error"
                      : "primary"
                }
                sx={{ marginLeft: "8px", fontSize: "11px" }}
              />
            )}
          </h2>
        </div>
      </div>

      {isOpen && (
        <div className={styles.cardContent}>
          <div style={{ paddingBottom: "16px" }}>
            {/* Description */}
            {description && (
              <p
                style={{
                  fontSize: "15px",
                  color: "#403f3f",
                  marginBottom: "16px",
                  lineHeight: "1.6",
                }}
              >
                <strong>Explanation:</strong> {description}
              </p>
            )}

            {/* Recommendation */}
            <CopyableText
              text={recommendation}
              label="Recommended clause"
              backgroundColor="#f0fbf9"
              borderColor="#B2E5DE"
              textColor="#004141"
              onAccept={onAccept}
              onReject={onReject}
              onModify={onModify}
            />

            {actionStatus === "Modified" &&
              modifyText &&
              modifyText !== recommendation && (
                <div style={{ marginTop: "12px" }}>
                  <CopyableText
                    text={modifyText}
                    label="Modified clause"
                    backgroundColor="#f0fbf9"
                    borderColor="#b2e5de"
                    textColor="#004141"
                    showActions={false}
                  />
                </div>
              )}

            {actionStatus === "Modifying" && (
              <div style={{ marginTop: "16px" }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Modify clause"
                  variant="outlined"
                  value={modifyText}
                  onChange={(e) => onModifyTextChange(e.target.value)}
                  size="small"
                  sx={{ marginBottom: "8px" }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={onModifySave}
                  sx={{
                    backgroundColor: "#08adc2",
                    "&:hover": { backgroundColor: "#4fb89a" },
                    textTransform: "none",
                  }}
                >
                  Save Modification
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Weak Clause Accordion Item
const WeakClauseItem = ({
  clauseId,
  title,
  severity,
  issue,
  suggestion,
  deviation,
  description,
  isOpen,
  onToggle,
  onAccept,
  onReject,
  onModify,
  actionStatus,
  modifyText,
  onModifyTextChange,
  onModifySave,
}: any) => {
  const severityColors = getSeverityColor(severity);

  return (
    <div className={styles.cardContainer}>
      <div className={styles.cardHeader}>
        <div
          onClick={onToggle}
          style={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            cursor: "pointer",
          }}
        >
          <h2 className={styles.cardTitle}>
            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            {/* <span style={{ fontWeight: "500", marginRight: "8px" }}>
              Rationale:
            </span> */}
            <span
              style={{
                color: "#00a888",
                fontWeight: "600",
                marginRight: "8px",
              }}
            >
              {clauseId}
            </span>
            {title}
            <Chip
              label={severityColors.label}
              size="small"
              sx={{
                backgroundColor: severityColors.bg,
                color: severityColors.text,
                border: `1px solid ${severityColors.border}`,
                fontWeight: 600,
                fontSize: "11px",
                marginLeft: "12px",
              }}
            />
            {actionStatus && (
              <Chip
                label={actionStatus}
                size="small"
                color={
                  actionStatus === "Accepted"
                    ? "success"
                    : actionStatus === "Rejected"
                      ? "error"
                      : "primary"
                }
                sx={{ marginLeft: "8px", fontSize: "11px" }}
              />
            )}
          </h2>
        </div>
      </div>

      {isOpen && (
        <div className={styles.cardContent}>
          <div style={{ paddingBottom: "16px" }}>
            {/* Deviation and Description Section */}
            {deviation && (
              <p
                style={{
                  fontSize: "15px",
                  color: "#403f3f",
                  marginBottom: "12px",
                  lineHeight: "1.6",
                }}
              >
                <strong>Deviation:</strong> {deviation}
              </p>
            )}
            {description && (
              <p
                style={{
                  fontSize: "15px",
                  color: "#403f3f",
                  marginBottom: "16px",
                  lineHeight: "1.6",
                }}
              >
                {description}
              </p>
            )}

            {/* Issue Description with mild red background */}
            <div
              style={{
                background: "#f0fbf9",
                border: "1px solid #B2E5DE",
                padding: "12px",
                borderRadius: "6px",
                marginBottom: "16px",
              }}
            >
              <p
                style={{
                  fontSize: "14px",
                  color: "#004141",
                  fontWeight: "500",
                  margin: 0,
                }}
              >
                <strong>Input draft:</strong> {issue}
              </p>
            </div>

            {/* Suggestion */}
            <CopyableText
              text={suggestion}
              label="Recommended redraft"
              backgroundColor="#f0fbf9"
              borderColor="#B2E5DE"
              textColor="#004141"
              onAccept={onAccept}
              onReject={onReject}
              onModify={onModify}
            />

            {actionStatus === "Modified" &&
              modifyText &&
              modifyText !== suggestion && (
                <div style={{ marginTop: "12px" }}>
                  <CopyableText
                    text={modifyText}
                    label="Modified redraft"
                    backgroundColor="#f0fbf9"
                    borderColor="#B2E5DE"
                    textColor="#004141"
                    showActions={false}
                  />
                </div>
              )}

            {actionStatus === "Modifying" && (
              <div style={{ marginTop: "16px" }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Modify redraft"
                  variant="outlined"
                  value={modifyText}
                  onChange={(e) => onModifyTextChange(e.target.value)}
                  size="small"
                  sx={{ marginBottom: "8px" }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={onModifySave}
                  sx={{
                    backgroundColor: "#08adc2",
                    "&:hover": { backgroundColor: "#4fb89a" },
                    textTransform: "none",
                  }}
                >
                  Save Modification
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Red Flag Accordion Item
const RedFlagItem = ({
  location,
  severity,
  description,
  mitigation,
  isOpen,
  onToggle,
  onAccept,
  onReject,
  onModify,
  actionStatus,
  modifyText,
  onModifyTextChange,
  onModifySave,
}: any) => {
  const severityColors = getSeverityColor(severity);

  return (
    <div className={styles.cardContainer}>
      <div className={styles.cardHeader}>
        <div
          onClick={onToggle}
          style={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            cursor: "pointer",
          }}
        >
          <h2 className={styles.cardTitle}>
            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            {/* <span style={{ fontWeight: "500", marginRight: "8px" }}>
              Rationale:
            </span> */}
            <span
              style={{
                color: "#bd1919",
                fontWeight: "600",
                marginRight: "8px",
              }}
            >
              {location}
            </span>
            <Chip
              label={severityColors.label}
              size="small"
              sx={{
                backgroundColor: severityColors.bg,
                color: severityColors.text,
                border: `1px solid ${severityColors.border}`,
                fontWeight: 600,
                fontSize: "11px",
                marginLeft: "12px",
              }}
            />
            {actionStatus && (
              <Chip
                label={actionStatus}
                size="small"
                color={
                  actionStatus === "Accepted"
                    ? "success"
                    : actionStatus === "Rejected"
                      ? "error"
                      : "primary"
                }
                sx={{ marginLeft: "8px", fontSize: "11px" }}
              />
            )}
          </h2>
        </div>
      </div>

      {isOpen && (
        <div className={styles.cardContent}>
          <div style={{ paddingBottom: "16px" }}>
            {/* Description */}
            <p
              style={{
                fontSize: "15px",
                color: "#403f3f",
                marginBottom: "16px",
                lineHeight: "1.6",
              }}
            >
              {description}
            </p>

            {/* Mitigation */}
            <CopyableText
              text={mitigation}
              label="Mitigation"
              backgroundColor="#dcfada"
              borderColor="#39a906"
              textColor="#267103"
              onAccept={onAccept}
              onReject={onReject}
              onModify={onModify}
            />

            {actionStatus === "Modified" &&
              modifyText &&
              modifyText !== mitigation && (
                <div style={{ marginTop: "12px" }}>
                  <CopyableText
                    text={modifyText}
                    label="Modified Mitigation"
                    backgroundColor="#e3f2fd"
                    borderColor="#00a888"
                    textColor="#00a888"
                    showActions={false}
                  />
                </div>
              )}

            {actionStatus === "Modifying" && (
              <div style={{ marginTop: "16px" }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Modify Mitigation"
                  variant="outlined"
                  value={modifyText}
                  onChange={(e) => onModifyTextChange(e.target.value)}
                  size="small"
                  sx={{ marginBottom: "8px" }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={onModifySave}
                  sx={{
                    backgroundColor: "#08adc2",
                    "&:hover": { backgroundColor: "#4fb89a" },
                    textTransform: "none",
                  }}
                >
                  Save Modification
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function TemplateReviewerAgent({
  parsedContent,
}: TemplateReviewerAgentProps): JSX.Element {
  const finalAnswer = safeJSONParse(parsedContent?.finalAnswer);
  const question = parsedContent?.question;
  const dispatch = useAppDispatch();

  // Get template reviewer feedback from Redux
  const { accept_array, reject_array, modify_array } = useSelector(
    (state: RootState) => state.filters.templateReviewerFeedback,
  );

  // State to track which accordion items are open
  const [openItems, setOpenItems] = useState<{ [key: string]: boolean }>({});

  // State for tracking action status of each item
  const [itemActions, setItemActions] = useState<{ [key: string]: string }>({});

  // State for modify text inputs
  const [modifyTexts, setModifyTexts] = useState<{ [key: string]: string }>({});

  // Function to download PDF from base64
  const downloadPDFFromBase64 = (base64String: string, filename: string) => {
    try {
      // Clean the base64 string - remove whitespace, newlines
      let cleanedBase64 = base64String
        .replace(/\s+/g, "") // Remove all whitespace
        .replace(/\n/g, "") // Remove newlines
        .replace(/\r/g, ""); // Remove carriage returns

      // Try to decode if URL-encoded
      try {
        cleanedBase64 = decodeURIComponent(cleanedBase64);
      } catch (e) {
        // If decoding fails, use the cleaned string as-is
      }

      // Decode base64 to binary
      const pdfBytes = atob(cleanedBase64);
      const byteArray = new Uint8Array(pdfBytes.length);
      for (let i = 0; i < pdfBytes.length; i++) {
        byteArray[i] = pdfBytes.charCodeAt(i);
      }

      // Create blob and download
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      console.log("PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF. Please try again.");
    }
  };

  if (!finalAnswer) {
    return <div>No data available</div>;
  }

  // Check if finalAnswer has accept_array or modify_array (final review results)
  if (
    (finalAnswer.accept_array && finalAnswer.accept_array.length > 0) ||
    (finalAnswer.modify_array && finalAnswer.modify_array.length > 0)
  ) {
    const acceptArray = finalAnswer.accept_array || [];
    const modifyArray = finalAnswer.modify_array || [];
    const pdfBase64 = finalAnswer.pdf_base64;
    const pdfFilename = finalAnswer.pdf_filename || "template_review.pdf";

    // Mark items with their source array before merging
    const acceptArrayWithSource = acceptArray.map((item: any) => ({
      ...item,
      _source: "accept",
    }));
    const modifyArrayWithSource = modifyArray.map((item: any) => ({
      ...item,
      _source: "modify",
    }));

    // Separate items based on whether they have deviation key
    const allItems = [...acceptArrayWithSource, ...modifyArrayWithSource];
    const weakClausesItems = allItems.filter((item) => item.deviation);
    const missingClausesItems = allItems.filter((item) => !item.deviation);

    return (
      <Box>
        <Paper
          elevation={0}
          sx={{
            padding: "24px",
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
            background: "#ffffff",
          }}
        >
          {/* Success Icon and Title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "20px",
              padding: "16px",
              borderRadius: "8px",
              background: "#f9fafb",
              border: "1px solid #e0e0e0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CheckCircleIcon
                sx={{
                  fontSize: "40px",
                  color: "#39a906",
                  marginRight: "16px",
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#004141",
                    marginBottom: "4px",
                  }}
                >
                  Review Complete
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#000",
                  }}
                >
                  Your selections have been processed successfully
                </Typography>
              </Box>
            </Box>

            {/* Download PDF Button */}
            {pdfBase64 && (
              <Tooltip title="Download PDF">
                <IconButton
                  onClick={() => downloadPDFFromBase64(pdfBase64, pdfFilename)}
                  sx={{
                    backgroundColor: "#00a888",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#008a70",
                    },
                  }}
                >
                  <DescriptionIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Missing Clauses Section */}
          {missingClausesItems.length > 0 && (
            <Box sx={{ marginTop: "20px" }}>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#004141",
                  marginBottom: "12px",
                  paddingBottom: "10px",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                Missing Clauses ({missingClausesItems.length})
              </Typography>
              {missingClausesItems.map((item: any, idx: number) => {
                const key = `result-missing-${idx}`;
                const isFromModifyArray = item._source === "modify";

                return (
                  <MissingPieceItem
                    key={idx}
                    section={item.section}
                    severity={item.severity}
                    recommendation={item.recommendation}
                    isOpen={openItems[key] || false}
                    onToggle={() => {
                      setOpenItems((prev) => ({
                        ...prev,
                        [key]: !prev[key],
                      }));
                    }}
                    actionStatus={isFromModifyArray ? "Modified" : "Accepted"}
                    modifyText={item.recommendation}
                    onAccept={undefined}
                    onReject={undefined}
                    onModify={undefined}
                  />
                );
              })}
            </Box>
          )}

          {/* Weak Clauses Section */}
          {weakClausesItems.length > 0 && (
            <Box sx={{ marginTop: "30px" }}>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#004141",
                  marginBottom: "12px",
                  paddingBottom: "10px",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                Weak Clauses ({weakClausesItems.length})
              </Typography>
              {weakClausesItems.map((item: any, idx: number) => {
                const key = `result-weak-${idx}`;
                const isFromModifyArray = item._source === "modify";

                return (
                  <WeakClauseItem
                    key={idx}
                    clauseId={item.clause_id}
                    title={item.title}
                    severity={item.severity}
                    issue={item.issue}
                    suggestion={item.suggestion}
                    deviation={undefined}
                    description={item.description}
                    isOpen={openItems[key] || false}
                    onToggle={() => {
                      setOpenItems((prev) => ({
                        ...prev,
                        [key]: !prev[key],
                      }));
                    }}
                    actionStatus={isFromModifyArray ? "Modified" : "Accepted"}
                    modifyText={item.suggestion}
                    onAccept={undefined}
                    onReject={undefined}
                    onModify={undefined}
                  />
                );
              })}
            </Box>
          )}

          {question && (
            <div
              style={{
                marginTop: "20px",
                paddingTop: "16px",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <p
                style={{
                  fontSize: "16px",
                  color: "#004141",
                  fontWeight: "600",
                  margin: 0,
                }}
              >
                {question}
              </p>
            </div>
          )}
        </Paper>
      </Box>
    );
  }

  const {
    missing_pieces = [],
    weak_clauses = [],
    red_flags = [],
    overall_score,
    overall_comments,
    executive_snapshot,
    summary_table = [],
  } = finalAnswer;

  // Get values from API response
  const overallReadiness = executive_snapshot?.overall_readiness || "Unknown";
  const highCount = executive_snapshot?.key_risks_count?.high || 0;
  const mediumCount = executive_snapshot?.key_risks_count?.medium || 0;

  const toggleItem = (section: string, index: number) => {
    const key = `${section}-${index}`;
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Helper function to create item key
  const getItemKey = (type: string, index: number) => `${type}-${index}`;

  // Accept handler
  const handleAccept = (type: string, index: number, item: any) => {
    const key = getItemKey(type, index);

    // Remove from other arrays if exists
    const newRejectArray = reject_array.filter((i) => i._key !== key);
    const newModifyArray = modify_array.filter((i) => i._key !== key);

    // Add to accept array
    const exists = accept_array.some((i) => i._key === key);
    const newAcceptArray = exists
      ? accept_array
      : [...accept_array, { ...item, _key: key }];

    // Update Redux
    dispatch(
      setTemplateReviewerFeedback({
        accept_array: newAcceptArray,
        reject_array: newRejectArray,
        modify_array: newModifyArray,
      }),
    );

    setItemActions((prev) => ({ ...prev, [key]: "Accepted" }));
  };

  // Reject handler
  const handleReject = (type: string, index: number, item: any) => {
    const key = getItemKey(type, index);

    // Remove from other arrays if exists
    const newAcceptArray = accept_array.filter((i) => i._key !== key);
    const newModifyArray = modify_array.filter((i) => i._key !== key);

    // Add to reject array
    const exists = reject_array.some((i) => i._key === key);
    const newRejectArray = exists
      ? reject_array
      : [...reject_array, { ...item, _key: key }];

    // Update Redux
    dispatch(
      setTemplateReviewerFeedback({
        accept_array: newAcceptArray,
        reject_array: newRejectArray,
        modify_array: newModifyArray,
      }),
    );

    setItemActions((prev) => ({ ...prev, [key]: "Rejected" }));
  };

  // Modify handler (initiates modify mode)
  const handleModify = (type: string, index: number, item: any) => {
    const key = getItemKey(type, index);

    // Set the action status to "Modifying" to show text input
    setItemActions((prev) => ({ ...prev, [key]: "Modifying" }));

    // Check if there's already a modified version in Redux
    const existingModifiedItem = modify_array.find((i) => i._key === key);

    // Initialize modify text with previously modified text if exists, otherwise use original
    let textToLoad = "";
    if (existingModifiedItem) {
      textToLoad =
        existingModifiedItem.recommendation ||
        existingModifiedItem.suggestion ||
        existingModifiedItem.mitigation ||
        "";
    } else {
      textToLoad =
        item.recommendation || item.suggestion || item.mitigation || "";
    }

    setModifyTexts((prev) => ({ ...prev, [key]: textToLoad }));

    // Open the accordion if not already open
    setOpenItems((prev) => ({
      ...prev,
      [`${type}-${index}`]: true,
    }));
  };

  // Save modification handler
  const handleModifySave = (type: string, index: number, item: any) => {
    const key = getItemKey(type, index);
    const modifiedText = modifyTexts[key];

    if (!modifiedText || modifiedText.trim() === "") {
      return;
    }

    // Remove from other arrays if exists
    const newAcceptArray = accept_array.filter((i) => i._key !== key);
    const newRejectArray = reject_array.filter((i) => i._key !== key);

    // Create modified item
    const modifiedItem = { ...item, _key: key };
    if (item.recommendation) {
      modifiedItem.recommendation = modifiedText;
    } else if (item.suggestion) {
      modifiedItem.suggestion = modifiedText;
    } else if (item.mitigation) {
      modifiedItem.mitigation = modifiedText;
    }

    // Add to modify array
    const exists = modify_array.some((i) => i._key === key);
    const newModifyArray = exists
      ? modify_array.map((i) => (i._key === key ? modifiedItem : i))
      : [...modify_array, modifiedItem];

    // Update Redux
    dispatch(
      setTemplateReviewerFeedback({
        accept_array: newAcceptArray,
        reject_array: newRejectArray,
        modify_array: newModifyArray,
      }),
    );

    setItemActions((prev) => ({ ...prev, [key]: "Modified" }));
  };

  return (
    <div>
      {/* Executive Summary Section */}
      <div style={{ marginTop: "20px", marginBottom: "30px" }}>
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#004141",
            marginBottom: "12px",
            paddingBottom: "10px",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          Executive Summary
        </h3>
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
          }}
        >
          {/* Overall Readiness */}
          <div style={{ marginBottom: "16px" }}>
            <span
              style={{
                fontSize: "15px",
                fontWeight: "600",
                color: "#004141",
              }}
            >
              • Overall Readiness:{" "}
            </span>
            <span
              style={{
                fontSize: "15px",
                fontWeight: "600",
                color:
                  overallReadiness === "Ready"
                    ? "#39a906"
                    : overallReadiness === "Conditionally Ready"
                      ? "#ffc107"
                      : "#dc3545",
              }}
            >
              {overallReadiness}
            </span>
          </div>

          {/* Key Risks Count */}
          <div>
            <span
              style={{
                fontSize: "15px",
                fontWeight: "600",
                color: "#004141",
              }}
            >
              • Key Risks Count:{" "}
            </span>
            <span
              style={{
                fontSize: "15px",
                fontWeight: "600",
                color: highCount > 0 ? "#dc3545" : "#403f3f",
              }}
            >
              High ({highCount})
            </span>
            <span style={{ margin: "0 8px", color: "#999" }}>|</span>
            <span
              style={{
                fontSize: "15px",
                fontWeight: "600",
                color: mediumCount > 0 ? "#ffc107" : "#403f3f",
              }}
            >
              Medium ({mediumCount})
            </span>
          </div>
        </div>
      </div>

      {/* Summary Section */}
      {summary_table && summary_table.length > 0 && (
        <div style={{ marginTop: "30px", marginBottom: "30px" }}>
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#004141",
              marginBottom: "12px",
              paddingBottom: "10px",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Summary
          </h3>

          {/* Table */}
          <div
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "6px",
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8f9fa" }}>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#004141",
                      borderBottom: "2px solid #e0e0e0",
                      width: "40%",
                    }}
                  >
                    Key Finding
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#004141",
                      borderBottom: "2px solid #e0e0e0",
                      width: "15%",
                    }}
                  >
                    Risk Level
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#004141",
                      borderBottom: "2px solid #e0e0e0",
                      width: "45%",
                    }}
                  >
                    Recommendation
                  </th>
                </tr>
              </thead>
              <tbody>
                {summary_table.map((item: any, idx: number) => {
                  const severityColors = getSeverityColor(item.risk_level);
                  return (
                    <tr
                      key={idx}
                      style={{
                        backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8f9fa",
                      }}
                    >
                      <td
                        style={{
                          padding: "12px",
                          fontSize: "14px",
                          color: "#403f3f",
                          borderBottom: "1px solid #e0e0e0",
                          verticalAlign: "top",
                          lineHeight: "1.6",
                        }}
                      >
                        {item.key_finding}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          fontSize: "14px",
                          borderBottom: "1px solid #e0e0e0",
                          verticalAlign: "top",
                        }}
                      >
                        <Chip
                          label={severityColors.label}
                          size="small"
                          sx={{
                            backgroundColor: severityColors.bg,
                            color: severityColors.text,
                            border: `1px solid ${severityColors.border}`,
                            fontWeight: 600,
                            fontSize: "11px",
                          }}
                        />
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          fontSize: "14px",
                          color: "#403f3f",
                          borderBottom: "1px solid #e0e0e0",
                          verticalAlign: "top",
                          lineHeight: "1.6",
                        }}
                      >
                        {item.recommendation}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Missing Clauses Section */}
      {missing_pieces.length > 0 && (
        <div style={{ marginTop: "20px" }}>
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#004141",
              marginBottom: "12px",
              paddingBottom: "10px",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Missing Clauses ({missing_pieces.length})
          </h3>
          {missing_pieces.map((piece: any, idx: number) => {
            const key = getItemKey("missing", idx);
            return (
              <MissingPieceItem
                key={idx}
                section={piece.section}
                severity={piece.severity}
                description={piece.description}
                recommendation={piece.recommendation}
                isOpen={openItems[`missing-${idx}`] || false}
                onToggle={() => toggleItem("missing", idx)}
                onAccept={() => handleAccept("missing", idx, piece)}
                onReject={() => handleReject("missing", idx, piece)}
                onModify={() => handleModify("missing", idx, piece)}
                actionStatus={itemActions[key]}
                modifyText={modifyTexts[key] || ""}
                onModifyTextChange={(text: string) =>
                  setModifyTexts((prev) => ({ ...prev, [key]: text }))
                }
                onModifySave={() => handleModifySave("missing", idx, piece)}
              />
            );
          })}
        </div>
      )}

      {/* Weak Clauses Section */}
      {weak_clauses.length > 0 && (
        <div style={{ marginTop: "30px" }}>
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#004141",
              marginBottom: "12px",
              paddingBottom: "10px",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Weak Clauses ({weak_clauses.length})
          </h3>
          {weak_clauses.map((clause: any, idx: number) => {
            const key = getItemKey("weak", idx);
            return (
              <WeakClauseItem
                key={idx}
                clauseId={clause.clause_id}
                title={clause.title}
                severity={clause.severity}
                issue={clause.issue}
                suggestion={clause.suggestion}
                deviation={clause.deviation}
                description={clause.description}
                isOpen={openItems[`weak-${idx}`] || false}
                onToggle={() => toggleItem("weak", idx)}
                onAccept={() => handleAccept("weak", idx, clause)}
                onReject={() => handleReject("weak", idx, clause)}
                onModify={() => handleModify("weak", idx, clause)}
                actionStatus={itemActions[key]}
                modifyText={modifyTexts[key] || ""}
                onModifyTextChange={(text: string) =>
                  setModifyTexts((prev) => ({ ...prev, [key]: text }))
                }
                onModifySave={() => handleModifySave("weak", idx, clause)}
              />
            );
          })}
        </div>
      )}

      {/* Red Flags Section */}
      {red_flags.length > 0 && (
        <div style={{ marginTop: "30px" }}>
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#004141",
              marginBottom: "12px",
              paddingBottom: "10px",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Critical Red Flags ({red_flags.length})
          </h3>
          {red_flags.map((flag: any, idx: number) => {
            const key = getItemKey("flag", idx);
            return (
              <RedFlagItem
                key={idx}
                location={flag.location}
                severity={flag.severity}
                description={flag.description}
                mitigation={flag.mitigation}
                isOpen={openItems[`flag-${idx}`] || false}
                onToggle={() => toggleItem("flag", idx)}
                onAccept={() => handleAccept("flag", idx, flag)}
                onReject={() => handleReject("flag", idx, flag)}
                onModify={() => handleModify("flag", idx, flag)}
                actionStatus={itemActions[key]}
                modifyText={modifyTexts[key] || ""}
                onModifyTextChange={(text: string) =>
                  setModifyTexts((prev) => ({ ...prev, [key]: text }))
                }
                onModifySave={() => handleModifySave("flag", idx, flag)}
              />
            );
          })}
        </div>
      )}

      {question && (
        <div
          style={{
            marginTop: "20px",
            paddingTop: "16px",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <p
            style={{
              fontSize: "16px",
              color: "#004141",
              fontWeight: "600",
              margin: 0,
            }}
          >
            {question}
          </p>
        </div>
      )}
    </div>
  );
}
